import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Loader2, X, Quote, Eye, EyeOff, ArrowUp, ArrowDown, Image } from 'lucide-react';
import { apiFetch } from '../lib/api';
import MediaLibraryPicker from '../components/admin/MediaLibraryPicker';

interface Testimonial {
  id: number;
  name: string;
  title?: string;
  organization?: string;
  quote: string;
  photoUrl?: string;
  sortOrder: number;
  isActive: number;
  createdAt: string;
}

const emptyForm = {
  name: '',
  title: '',
  organization: '',
  quote: '',
  photoUrl: '',
  sortOrder: 0,
  isActive: 1,
};

export default function AdminTestimonials() {
  const [items, setItems] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchItems = async () => {
    setLoading(true);
    const res = await apiFetch<Testimonial[]>('/api/admin/testimonials');
    if (res.success && res.data) {
      setItems(res.data);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (item: Testimonial) => {
    setEditingId(item.id);
    setForm({
      name: item.name,
      title: item.title || '',
      organization: item.organization || '',
      quote: item.quote,
      photoUrl: item.photoUrl || '',
      sortOrder: item.sortOrder || 0,
      isActive: item.isActive,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.quote.trim()) {
      alert('請輸入姓名和見證內容');
      return;
    }
    setSaving(true);
    const url = editingId
      ? `/api/admin/testimonials/${editingId}`
      : '/api/admin/testimonials';
    const method = editingId ? 'PUT' : 'POST';
    const res = await apiFetch(url, {
      method,
      body: JSON.stringify(form),
    });
    setSaving(false);
    if (res.success) {
      setModalOpen(false);
      fetchItems();
    } else {
      alert(res.error?.message || '保存失敗');
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('確定要刪除這個見證嗎？')) return;
    const res = await apiFetch(`/api/admin/testimonials/${id}`, { method: 'DELETE' });
    if (res.success) {
      fetchItems();
    } else {
      alert(res.error?.message || '刪除失敗');
    }
  };

  const toggleActive = async (item: Testimonial) => {
    const res = await apiFetch(`/api/admin/testimonials/${item.id}`, {
      method: 'PUT',
      body: JSON.stringify({ isActive: item.isActive ? 0 : 1 }),
    });
    if (res.success) fetchItems();
  };

  const moveOrder = async (item: Testimonial, direction: 'up' | 'down') => {
    const currentIndex = items.findIndex((i) => i.id === item.id);
    if (currentIndex === -1) return;
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= items.length) return;

    const targetItem = items[targetIndex];

    // Swap sort orders
    await Promise.all([
      apiFetch(`/api/admin/testimonials/${item.id}`, {
        method: 'PUT',
        body: JSON.stringify({ sortOrder: targetItem.sortOrder }),
      }),
      apiFetch(`/api/admin/testimonials/${targetItem.id}`, {
        method: 'PUT',
        body: JSON.stringify({ sortOrder: item.sortOrder }),
      }),
    ]);
    fetchItems();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Quote className="w-6 h-6 text-brand-600" />
          權威見證管理
        </h1>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 bg-brand-600 hover:bg-brand-700 text-white font-bold py-2.5 px-5 rounded-xl transition-colors"
        >
          <Plus className="w-4 h-4" />
          新增見證
        </button>
      </div>

      {/* List */}
      <div className="bg-white rounded-2xl shadow-sm border border-brand-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-gray-500">
                <th className="px-4 py-3 text-left font-medium w-10">排序</th>
                <th className="px-4 py-3 text-left font-medium">照片</th>
                <th className="px-4 py-3 text-left font-medium">姓名</th>
                <th className="px-4 py-3 text-left font-medium">機構</th>
                <th className="px-4 py-3 text-left font-medium">見證摘要</th>
                <th className="px-4 py-3 text-center font-medium">狀態</th>
                <th className="px-4 py-3 text-right font-medium">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 7 }).map((_, j) => (
                      <td key={j} className="px-4 py-3">
                        <span className="inline-block w-full h-4 bg-gray-100 rounded animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : items.length > 0 ? (
                items.map((item, index) => (
                  <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={() => moveOrder(item, 'up')}
                          disabled={index === 0}
                          className="text-gray-400 hover:text-brand-600 disabled:opacity-30 disabled:hover:text-gray-400"
                          title="上移"
                        >
                          <ArrowUp className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => moveOrder(item, 'down')}
                          disabled={index === items.length - 1}
                          className="text-gray-400 hover:text-brand-600 disabled:opacity-30 disabled:hover:text-gray-400"
                          title="下移"
                        >
                          <ArrowDown className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {item.photoUrl ? (
                        <img
                          src={item.photoUrl}
                          alt={item.name}
                          className="w-10 h-10 rounded-full object-cover border border-gray-200"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center border border-gray-200">
                          <Quote className="w-4 h-4 text-gray-300" />
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{item.name}</div>
                      {item.title && (
                        <div className="text-xs text-gray-500">{item.title}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{item.organization || '—'}</td>
                    <td className="px-4 py-3">
                      <div className="text-gray-600 line-clamp-2 max-w-[280px]">{item.quote}</div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => toggleActive(item)}
                        className={`inline-flex items-center justify-center w-8 h-8 rounded-lg transition-colors ${
                          item.isActive
                            ? 'text-green-600 hover:bg-green-50'
                            : 'text-gray-400 hover:bg-gray-100'
                        }`}
                        title={item.isActive ? '顯示中' : '已隱藏'}
                      >
                        {item.isActive ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEdit(item)}
                          className="p-2 text-gray-500 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-colors"
                          title="編輯"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="刪除"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-400">
                    暫無權威見證，點擊「新增見證」開始添加
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setModalOpen(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">
                {editingId ? '編輯見證' : '新增見證'}
              </h2>
              <button
                onClick={() => setModalOpen(false)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">姓名 *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm"
                  placeholder="例如：陳校長"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">職稱</label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                    className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm"
                    placeholder="例如：特殊教育學校校長"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">機構</label>
                  <input
                    type="text"
                    value={form.organization}
                    onChange={(e) => setForm((f) => ({ ...f, organization: e.target.value }))}
                    className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm"
                    placeholder="例如：社區服務中心"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">見證內容 *</label>
                <textarea
                  value={form.quote}
                  onChange={(e) => setForm((f) => ({ ...f, quote: e.target.value }))}
                  rows={5}
                  className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm"
                  placeholder="這位權威人士的見證內容..."
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm font-medium text-gray-700">照片 URL</label>
                  <button
                    type="button"
                    onClick={() => setPickerOpen(true)}
                    className="text-xs flex items-center gap-1 text-brand-600 hover:text-brand-700 font-medium"
                  >
                    <Image className="w-3.5 h-3.5" />
                    從媒體庫選擇
                  </button>
                </div>
                <input
                  type="text"
                  value={form.photoUrl}
                  onChange={(e) => setForm((f) => ({ ...f, photoUrl: e.target.value }))}
                  className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm"
                  placeholder="https://..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">排序</label>
                  <input
                    type="number"
                    value={form.sortOrder}
                    onChange={(e) => setForm((f) => ({ ...f, sortOrder: Number(e.target.value) }))}
                    className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none text-sm"
                  />
                </div>
                <div className="flex items-center gap-2 pt-7">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={!!form.isActive}
                    onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked ? 1 : 0 }))}
                    className="w-4 h-4 text-brand-600 border-gray-300 rounded focus:ring-brand-500"
                  />
                  <label htmlFor="isActive" className="text-sm text-gray-700">
                    前台顯示
                  </label>
                </div>
              </div>
            </div>
            <MediaLibraryPicker
              open={pickerOpen}
              onClose={() => setPickerOpen(false)}
              filterType="image"
              onSelect={(url) => setForm((f) => ({ ...f, photoUrl: url }))}
            />
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
              <button
                onClick={() => setModalOpen(false)}
                className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-xl transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-2 bg-brand-600 hover:bg-brand-700 text-white font-bold py-2.5 px-5 rounded-xl transition-colors disabled:opacity-50"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {saving ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
